import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  socialTitle?: string; // Separate title for social media sharing
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "restaurant";
  structuredData?: object;
}

const defaultSEO = {
  title: "Home | Ristorante Tennis & Gourmet Morbegno",
  description:
    "Ristorante Morbegno con piscina e padel in Valtellina. Tennis Gourmet: cucina di pesce, pizzeria gourmet, centro sportivo con piscina Morbegno e padel Valtellina. Prenota ora!",
  keywords:
    "ristorante morbegno, ristorante valtellina, piscina morbegno, piscina valtellina, padel valtellina, tennis morbegno, pizzeria gourmet morbegno, centro sportivo valtellina, ristorante pesce morbegno",
  image: "/images/home-hero.webp",
  url: "https://www.ristorantetennisgourmet.it",
  type: "website" as const,
};

export function SEO({
  title,
  socialTitle,
  description,
  keywords,
  image,
  url,
  type = "website",
  structuredData,
}: SEOProps) {
  // Generate browser tab title with consistent brand structure
  const generateBrowserTitle = (pageTitle?: string) => {
    if (!pageTitle) return defaultSEO.title;
    return `${pageTitle} | Ristorante Tennis & Gourmet Morbegno`;
  };

  // Generate social media title with consistent brand structure
  const generateSocialTitle = (pageTitle?: string) => {
    if (socialTitle) return socialTitle;
    if (!pageTitle) return "Ristorante Tennis & Gourmet Morbegno | Home";
    return `Ristorante Tennis & Gourmet Morbegno | ${pageTitle}`;
  };

  const seo = {
    title: title ? generateBrowserTitle(title) : defaultSEO.title,
    socialTitle: generateSocialTitle(title),
    description: description || defaultSEO.description,
    keywords: keywords || defaultSEO.keywords,
    image: image || defaultSEO.image,
    url: url || defaultSEO.url,
    type,
  };

  const fullImageUrl = seo.image.startsWith("http")
    ? seo.image
    : `${defaultSEO.url}${seo.image}`;

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={seo.url} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={seo.type} />
      <meta property="og:title" content={seo.socialTitle} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:site_name" content="Ristorante Tennis & Gourmet Morbegno" />
      <meta property="og:locale" content="it_IT" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.socialTitle} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Tennis Gourmet" />
      <meta name="language" content="Italian" />

      {/* Structured Data - Only add if different from base HTML */}
      {structuredData && url !== defaultSEO.url && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
}
